// src/services/eventAPI.js
import { API_URL } from '../config';

const API_BASE_URL = API_URL;

async function parseApiResponse(response) {
  const contentType = response.headers.get('content-type') || '';

  if (response.ok) {
    return contentType.includes('application/json')
      ? response.json()
      : {};
  }

  let message = `HTTP error! status: ${response.status}`;

  try {
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (data?.message) message = data.message;
    } else {
      const text = await response.text();
      if (text) message = text;
    }
  } catch {
    // Fall back to the default message when the error body can't be parsed.
  }

  if (response.status === 413) {
    message = 'Upload is too large for the hosted server limit. Reduce the cover, gallery images, or videos and try again.';
  }

  throw new Error(message);
}

// ============================================
// EVENTS API
// ============================================
export function formatEventDate(dateStr, format = 'full') {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (format === 'month-year') {
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    // → "March 2026"
  }
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  // → "27 March 2026"
}

export const eventAPI = {
  // Get all events with optional filters
  getAll: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await fetch(`${API_BASE_URL}/events?${params}`);
      const data = await parseApiResponse(response);
      return {
        success: data.success !== false,
        data: Array.isArray(data.data) ? data.data : [],
        message: data.message || 'Success',
      };
    } catch (error) {
      console.error('Error fetching events:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  // Get upcoming events
  getUpcoming: async () => {
    return eventAPI.getAll({ category: 'upcoming', status: 'active' });
  },

  // Get past events
  getPast: async () => {
    return eventAPI.getAll({ category: 'past', status: 'active' });
  },

  // Get single event by ID
  getById: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${id}`);
      const data = await parseApiResponse(response);
      return {
        success: data.success !== false,
        data: data.data || null,
        message: data.message || 'Success',
      };
    } catch (error) {
      console.error('Error fetching event:', error);
      return { success: false, data: null, message: error.message };
    }
  },

  // Get galleries for an event
  getGalleries: async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters).toString();
      const response = await fetch(`${API_BASE_URL}/galleries?${params}`);
      const data = await parseApiResponse(response);
      return {
        success: data.success !== false,
        data: Array.isArray(data.data) ? data.data : [],
        message: data.message || 'Success',
      };
    } catch (error) {
      console.error('Error fetching galleries:', error);
      return { success: false, data: [], message: error.message };
    }
  },

  // ✅ UPDATED: Create event — supports image file upload via FormData
  create: async (eventData, imageFile = null) => {
    try {
      const formData = new FormData();

      // Append all event fields
      Object.entries(eventData).forEach(([key, val]) => {
        if (val !== null && val !== undefined) {
          formData.append(key, val);
        }
      });

      // Append image file if provided
      if (imageFile) {
        formData.append('image', imageFile); // must match upload.single('image') in backend
      }

      const response = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        // ⚠️ Do NOT set Content-Type header — browser sets it automatically with boundary
        body: formData,
      });
      const data = await parseApiResponse(response);
      return {
        success: data.success !== false,
        data: data.data || null,
        message: data.message || 'Success',
      };
    } catch (error) {
      console.error('Error creating event:', error);
      return { success: false, data: null, message: error.message };
    }
  },

  // ✅ UPDATED: Update event — supports image file upload via FormData
  update: async (id, eventData, imageFile = null) => {
    try {
      const formData = new FormData();

      // Append all event fields
      Object.entries(eventData).forEach(([key, val]) => {
        if (val !== null && val !== undefined) {
          formData.append(key, val);
        }
      });

      // Append new image file if provided
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await fetch(`${API_BASE_URL}/events/${id}`, {
        method: 'PUT',
        // ⚠️ Do NOT set Content-Type header
        body: formData,
      });
      const data = await parseApiResponse(response);
      return {
        success: data.success !== false,
        data: data.data || null,
        message: data.message || 'Success',
      };
    } catch (error) {
      console.error('Error updating event:', error);
      return { success: false, data: null, message: error.message };
    }
  },

  // Delete event (admin only) — unchanged
  delete: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${id}`, {
        method: 'DELETE',
      });
      const data = await parseApiResponse(response);
      return {
        success: data.success !== false,
        message: data.message || 'Success',
      };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { success: false, message: error.message };
    }
  },
  // Accepts a pre-built FormData object directly
createWithFormData: async (formData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/events`, {
      method: 'POST',
      body: formData, // no Content-Type header — browser sets it with boundary
    });
    const data = await parseApiResponse(response);
    return {
      success: data.success !== false,
      data: data.data || null,
      message: data.message || 'Success',
    };
  } catch (error) {
    console.error('Error creating event:', error);
    return { success: false, data: null, message: error.message };
  }
},

updateWithFormData: async (id, formData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/events/${id}`, {
      method: 'PUT',
      body: formData,
    });
    const data = await parseApiResponse(response);
    return {
      success: data.success !== false,
      data: data.data || null,
      message: data.message || 'Success',
    };
  } catch (error) {
    console.error('Error updating event:', error);
    return { success: false, data: null, message: error.message };
  }
},

};

export default eventAPI;
